import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { platformAPI } from '../../lib/platformApi';
import { Upload, Check, Clock, FileText, Building2, User } from 'lucide-react';

const steps = [
  { key: 'business_details', label: 'Business Details', icon: Building2 },
  { key: 'director_details', label: 'Director Details', icon: User },
  { key: 'documents', label: 'Upload Documents', icon: FileText },
  { key: 'review', label: 'Submit for Review', icon: Check },
];

const requiredDocs = [
  { key: 'cac_certificate', label: 'CAC Certificate', description: 'Certificate of incorporation' },
  { key: 'memart', label: 'MEMART / Articles', description: 'Memorandum and articles of association' },
  { key: 'director_id', label: 'Director ID', description: 'Valid government-issued photo ID' },
  { key: 'proof_of_address', label: 'Proof of Address', description: 'Utility bill or bank statement (< 3 months)' },
  { key: 'board_resolution', label: 'Board Resolution (optional)', description: 'If company has multiple directors' },
];

export default function PlatformGoLive() {
  const [currentStep, setCurrentStep] = useState(0);
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    registered_business_name: '', rc_number: '', tax_identification_number: '',
    business_address: '', director_full_name: '', director_bvn: '',
    director_phone: '', director_email: '',
  });

  const loadApplication = useCallback(async () => {
    try {
      const { data } = await platformAPI.getApplication();
      setApplication(data);
      setForm(prev => ({
        ...prev,
        registered_business_name: data.registered_business_name || '',
        rc_number: data.rc_number || '',
        business_address: data.business_address || '',
        director_full_name: data.director_full_name || '',
      }));
      if (data.status === 'submitted' || data.status === 'under_review') setCurrentStep(3);
      else if (data.documents && Object.values(data.documents).some(Boolean)) setCurrentStep(2);
      else if (data.director_full_name) setCurrentStep(2);
      else if (data.registered_business_name) setCurrentStep(1);
    } catch {
      // Application might not exist yet for new tenants — that's ok
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadApplication(); }, [loadApplication]);

  const saveDetails = async () => {
    setSaving(true);
    try {
      await platformAPI.updateApplication(form);
      toast.success('Details saved');
      if (currentStep < 3) setCurrentStep(currentStep + 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const uploadDoc = async (docType, file) => {
    try {
      await platformAPI.uploadDocument(docType, file);
      toast.success(`${docType.replace(/_/g, ' ')} uploaded`);
      loadApplication();
    } catch {
      toast.error('Upload failed');
    }
  };

  const submitForReview = async () => {
    setSaving(true);
    try {
      await platformAPI.submitApplication();
      toast.success("Application submitted! We'll review within 48-72 hours.");
      loadApplication();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-center py-12 text-gray-400">Loading...</div>;

  if (application?.status === 'submitted' || application?.status === 'under_review') {
    return (
      <div className="max-w-2xl" data-testid="go-live-under-review">
        <div className="text-center py-12">
          <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
            <Clock size={32} className="text-blue-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Application Under Review</h2>
          <p className="text-gray-500">We're reviewing your documents. This typically takes 48-72 hours. We'll email you when you're approved.</p>
        </div>
      </div>
    );
  }

  if (application?.status === 'approved') {
    return (
      <div className="max-w-2xl" data-testid="go-live-approved">
        <div className="text-center py-12">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
            <Check size={32} className="text-green-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>You're Live!</h2>
          <p className="text-gray-500 mb-4">Your platform is approved for production banking.</p>
          <a href="/platform/api-keys" className="inline-flex px-4 py-2 bg-[#E8678B] text-white rounded-lg text-sm font-semibold hover:bg-[#d15a7d]">
            View Live API Keys &rarr;
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl" data-testid="platform-go-live-page">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Go Live</h1>
        <p className="text-gray-500 mt-1">Complete verification to enable real banking</p>
      </div>

      {/* Step indicators */}
      <div className="flex items-center gap-2 mb-8 flex-wrap" data-testid="step-indicators">
        {steps.map((step, i) => (
          <React.Fragment key={step.key}>
            <button
              onClick={() => setCurrentStep(i)}
              data-testid={`step-btn-${step.key}`}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                i === currentStep ? 'bg-[#E8678B] text-white' :
                i < currentStep ? 'bg-pink-50 text-[#E8678B]' :
                'bg-gray-100 text-gray-400'
              }`}
            >
              <step.icon size={16} />
              <span className="hidden sm:inline">{step.label}</span>
            </button>
            {i < steps.length - 1 && <div className="flex-1 h-px bg-gray-200 min-w-[16px]" />}
          </React.Fragment>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-6">
        {currentStep === 0 && (
          <div className="space-y-4" data-testid="step-business-details">
            <h3 className="text-lg font-semibold text-gray-900">Business Details</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Registered Business Name</label>
              <input type="text" value={form.registered_business_name} onChange={e => setForm({...form, registered_business_name: e.target.value})}
                data-testid="input-business-name"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#E8678B]/20 focus:border-[#E8678B]"
                placeholder="As it appears on your CAC certificate" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">RC Number</label>
              <input type="text" value={form.rc_number} onChange={e => setForm({...form, rc_number: e.target.value})}
                data-testid="input-rc-number"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#E8678B]/20 focus:border-[#E8678B]"
                placeholder="RC 1234567" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tax ID (optional)</label>
              <input type="text" value={form.tax_identification_number} onChange={e => setForm({...form, tax_identification_number: e.target.value})}
                data-testid="input-tax-id"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#E8678B]/20 focus:border-[#E8678B]"
                placeholder="TIN" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Business Address</label>
              <textarea value={form.business_address} onChange={e => setForm({...form, business_address: e.target.value})}
                data-testid="input-business-address"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#E8678B]/20 focus:border-[#E8678B]" rows={3}
                placeholder="Full registered address" />
            </div>
            <button onClick={saveDetails} disabled={saving}
              data-testid="save-business-details-btn"
              className="w-full py-2.5 bg-[#E8678B] text-white rounded-lg text-sm font-semibold hover:bg-[#d15a7d] disabled:opacity-50">
              {saving ? 'Saving...' : 'Save & Continue'}
            </button>
          </div>
        )}

        {currentStep === 1 && (
          <div className="space-y-4" data-testid="step-director-details">
            <h3 className="text-lg font-semibold text-gray-900">Director / Signatory Details</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
              <input type="text" value={form.director_full_name} onChange={e => setForm({...form, director_full_name: e.target.value})}
                data-testid="input-director-name"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#E8678B]/20 focus:border-[#E8678B]"
                placeholder="As it appears on ID" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">BVN</label>
              <input type="text" value={form.director_bvn} onChange={e => setForm({...form, director_bvn: e.target.value})} maxLength={11}
                data-testid="input-director-bvn"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#E8678B]/20 focus:border-[#E8678B]"
                placeholder="11-digit Bank Verification Number" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input type="tel" value={form.director_phone} onChange={e => setForm({...form, director_phone: e.target.value})}
                data-testid="input-director-phone"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#E8678B]/20 focus:border-[#E8678B]"
                placeholder="+234..." />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input type="email" value={form.director_email} onChange={e => setForm({...form, director_email: e.target.value})}
                data-testid="input-director-email"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#E8678B]/20 focus:border-[#E8678B]"
                placeholder="director@company.com" />
            </div>
            <button onClick={saveDetails} disabled={saving}
              data-testid="save-director-details-btn"
              className="w-full py-2.5 bg-[#E8678B] text-white rounded-lg text-sm font-semibold hover:bg-[#d15a7d] disabled:opacity-50">
              {saving ? 'Saving...' : 'Save & Continue'}
            </button>
          </div>
        )}

        {currentStep === 2 && (
          <div className="space-y-4" data-testid="step-documents">
            <h3 className="text-lg font-semibold text-gray-900">Upload Documents</h3>
            <p className="text-sm text-gray-500">Upload clear photos or scans (PDF, JPG, PNG). Max 10MB each.</p>
            {requiredDocs.map(doc => (
              <div key={doc.key} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg" data-testid={`doc-row-${doc.key}`}>
                <div className="flex items-center gap-3">
                  {application?.documents?.[doc.key] ? (
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                      <Check size={16} className="text-green-600" />
                    </div>
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                      <Upload size={16} className="text-gray-400" />
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-medium text-gray-900">{doc.label}</p>
                    <p className="text-xs text-gray-500">{doc.description}</p>
                  </div>
                </div>
                <label className="cursor-pointer shrink-0">
                  <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png"
                    data-testid={`upload-input-${doc.key}`}
                    onChange={e => e.target.files[0] && uploadDoc(doc.key, e.target.files[0])} />
                  <span className={`text-sm font-medium px-3 py-1.5 rounded-lg transition-colors ${
                    application?.documents?.[doc.key]
                      ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      : 'bg-pink-50 text-[#E8678B] hover:bg-pink-100'
                  }`}>
                    {application?.documents?.[doc.key] ? 'Replace' : 'Upload'}
                  </span>
                </label>
              </div>
            ))}
            <button onClick={() => setCurrentStep(3)}
              data-testid="continue-to-review-btn"
              className="w-full py-2.5 bg-[#E8678B] text-white rounded-lg text-sm font-semibold hover:bg-[#d15a7d]">
              Continue to Review
            </button>
          </div>
        )}

        {currentStep === 3 && (
          <div className="space-y-4" data-testid="step-review">
            <h3 className="text-lg font-semibold text-gray-900">Review & Submit</h3>
            <p className="text-sm text-gray-500">Review your details. Once submitted, we'll review within 48-72 hours.</p>

            <div className="space-y-3">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500">Business Name</p>
                <p className="text-sm font-medium text-gray-900">{form.registered_business_name || '\u2014'}</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500">RC Number</p>
                <p className="text-sm font-medium text-gray-900">{form.rc_number || '\u2014'}</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500">Director</p>
                <p className="text-sm font-medium text-gray-900">{form.director_full_name || '\u2014'}</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500">Documents</p>
                <div className="flex gap-2 mt-1 flex-wrap">
                  {requiredDocs.filter(d => d.key !== 'board_resolution').map(d => (
                    <span key={d.key} className={`text-xs px-2 py-1 rounded ${application?.documents?.[d.key] ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {d.label}: {application?.documents?.[d.key] ? 'Uploaded' : 'Missing'}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <button onClick={submitForReview} disabled={saving}
              data-testid="submit-application-btn"
              className="w-full py-3 bg-[#E8678B] text-white rounded-lg text-sm font-bold hover:bg-[#d15a7d] disabled:opacity-50">
              {saving ? 'Submitting...' : 'Submit Application'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
